import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../../../hooks/NotificationContext';
import { isExecutiveManager } from '../../../../lib/userUtils';
import { getTestCaseData } from '../../../../modules/config';
import { updateTestCases } from '../../../../modules/jira';
import { getAssociatedUsers, getCurrentProject } from '../../../../modules/project';
import DetailsCard from '../../../../ui/layout/DetailsCard';
import CreateTestCaseForm from '../CreateTestCaseForm';

const TestCaseDetails = ({
  data,
  fetchTestCaseData,
  testCaseId,
  projectId,
  onUnsavedStateChange,
  saveRef,
  initialEditMode = false,
}) => {
  const defaultFormData = {
    objective: data?.objective || '',
    summary: data?.summary || '',
    preCondition: data?.pre_condition || '',
    labels: data?.labels || [],
    testsuites: data?.test_suites || [],
    priority: data?.priority?.id || null,
    status: data?.status?.id || null,
    module: data?.module?.id || null,
    owner: data?.owner || '',
    estimatedTime: data?.estimated_time || '',
    isAutomated: data?.is_automated || 'No',
    reasonForNotAutomated: data?.reason_for_not_automated || '',
    technologyLayer: data?.technology_layer_details?.id || '',
    reviewer: data?.reviewer || '',
    approvedBy: data?.approved_by || '',
    automatedBy: data?.automated_by || '',
    automationMethod: data?.automation_method || null,
    automationStatus: data?.automation_status || null,
    isAutomationNeeded: data?.is_automation_needed || false,
    country: data?.country || [],
    automationCountry: data?.automation_country || [],
    notAutomatedCategory: data?.not_automated_category || null,
    notAutomatedSubCategory: data?.not_automated_sub_category || null,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [isFormEdited, setIsFormEdited] = useState(false);
  const { addNotification } = useNotification();
  const [statuses, setStatuses] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [modules, setModules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [isReviewWorkflow, setIsReviewWorkflow] = useState(false);
  const [technologyLayers, setTechnologyLayers] = useState([]);
  const validateAllRef = useRef(null);

  // Keep a fresh ref so the parent's saveRef always calls the latest handleSaveClick
  const handleSaveClickRef = useRef(null);

  const fetchProjectData = async () => {
    const response = await getCurrentProject(projectId);
    setIsReviewWorkflow(response.review_workflow);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsFormEdited(false);
    setFormData(defaultFormData);
    // Immediately clear parent's unsaved guard
    onUnsavedStateChange?.(false, false);
  };

  const getTestCasesData = async () => {
    setLoading(true);
    try {
      const response = await getTestCaseData(projectId);
      if (response) {
        setTestSuites(response?.test_suites);
        setStatuses(response?.result.filter((item) => item.group === 'TEST_CASE_STATUS'));
        setPriorities(response?.result.filter((item) => item.group === 'TEST_CASE_PRIORITY'));
        setLabels(response?.result.filter((item) => item.group === 'LABEL'));
        setModules(response?.result.filter((item) => item.group === 'FUNCTIONAL_MODULE_FIELD'));
        setTechnologyLayers(response?.result.filter((item) => item.group === 'TECHNOLOGY_LAYER'));
      }
      const usersResponse = await getAssociatedUsers(projectId);
      if (usersResponse) {
        setProjectUsers(
          usersResponse.map((userData) => ({ ...userData.user, role: userData.role }))
        );
      }
    } catch (error) {
      console.error('Error fetching test case data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = async () => {
    const errors = validateAllRef.current?.();
    if (errors && Object.keys(errors).length > 0) {
      addNotification('danger', 'Please fill in all required fields before saving.');
      return;
    }

    if (!formData.summary.trim()) {
      addNotification('danger', 'Please add description');
      return;
    }
    try {
      const testcase = {
        ...formData,
        reasonForNotAutomated:
          formData.isAutomated === 'No' ? (formData.reasonForNotAutomated || '').trim() : '',
      };
      await updateTestCases({ testcase }, testCaseId);
      addNotification('success', 'Test case updated successfully!');
      fetchTestCaseData();
      setIsEditing(false);
      setIsFormEdited(false);
      onUnsavedStateChange?.(false, false);
    } catch (error) {
      const errorMessage = error?.message || 'Failed to update test case. Please try again later.';
      addNotification('danger', errorMessage);
    }
  };

  handleSaveClickRef.current = handleSaveClick;

  useEffect(() => {
    if (saveRef) {
      saveRef.current = () => handleSaveClickRef.current();
    }
  }, [saveRef]);

  useEffect(() => {
    onUnsavedStateChange?.(isEditing, isFormEdited);
  }, [isEditing, isFormEdited]);

  useEffect(() => {
    getTestCasesData();
    fetchProjectData();
  }, []);

  return (
    <DetailsCard
      title="Test Case Details"
      actions={
        isEditing ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSaveClick}
            >
              Save
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        ) : (
          !isExecutiveManager() &&
          !data?.is_archived && (
            <Button variant="contained" onClick={handleEditClick}>
              Edit
            </Button>
          )
        )
      }
    >
      <CreateTestCaseForm
        formData={formData}
        isEditing={isEditing}
        setFormData={setFormData}
        defaultFormData={defaultFormData}
        setIsFormEdited={setIsFormEdited}
        isFormEdited={isFormEdited}
        testSuites={testSuites}
        projectUsers={projectUsers}
        statuses={statuses}
        priorities={priorities}
        labels={labels}
        modules={modules}
        loading={loading}
        isReviewWorkflow={isReviewWorkflow}
        refetchData={getTestCasesData}
        technologyLayers={technologyLayers}
        validateAllRef={validateAllRef}
      />
    </DetailsCard>
  );
};

export default TestCaseDetails;